const numberFormat = new Intl.NumberFormat();
const compactFormat = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});
const dateFormat = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
});

export function formatNumber(value: number | null | undefined): string {
  return numberFormat.format(value ?? 0);
}

/** Compact form for tight spots — axis ticks and stat tiles above 5 digits. */
export function formatCompact(value: number | null | undefined): string {
  const n = value ?? 0;
  return n < 10_000 ? numberFormat.format(n) : compactFormat.format(n);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateTimeFormat.format(date);
}

export function formatDay(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : dateFormat.format(date);
}

/** "3 minutes ago" / "in 5 days" — for last-seen and expiry columns. */
export function formatRelative(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const seconds = (date.getTime() - Date.now()) / 1000;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  const relative = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) {
      return relative.format(Math.round(seconds / size), unit);
    }
  }
  return relative.format(Math.round(seconds), 'second');
}

/** The operator's UTC offset in minutes, as the dashboard endpoint expects. */
export function timezoneOffsetMinutes(): number {
  return -new Date().getTimezoneOffset();
}

/** Midnight local time today, as an ISO instant for `registered_from`. */
export function startOfTodayIso(): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
}

/** Midnight local time [days] ago, as an ISO instant. */
export function daysAgoIso(days: number): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - days);
  return date.toISOString();
}

/** A percentage of a total, guarding the zero-total case. */
export function percentOf(value: number, total: number): string {
  if (!total) return '0%';
  return `${Math.round((value / total) * 100)}%`;
}

/** Platform names as their vendors spell them; `capitalize` gets iOS wrong. */
export function formatPlatform(platform: string): string {
  return platform === 'ios' ? 'iOS' : 'Android';
}
